import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, HStack, Badge, Table, Thead, Tbody, Tr, Th, Td, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { FaBuilding, FaIndustry, FaTruck, FaStore, FaWarehouse, FaChartLine } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const GRAPHITE = "#141C49";
const SAND = "#C2A878";

const industries = [
  { icon: FaBuilding, title: "Office & Retail", desc: "Finance for commercial strata, shopping centres, and office buildings with competitive LVRs.", color: GRAPHITE },
  { icon: FaIndustry, title: "Industrial & Warehouse", desc: "Manufacturing facilities, warehouses, and logistics centres with flexible terms.", color: GRAPHITE },
  { icon: FaTruck, title: "Transport & Logistics", desc: "Fleet finance, truck loans, and equipment for transport businesses.", color: SAND },
  { icon: FaStore, title: "Retail & Hospitality", desc: "Shop fit-outs, restaurant equipment, and business acquisition finance.", color: SAND },
  { icon: FaWarehouse, title: "Manufacturing", desc: "Machinery, production equipment, and facility expansion finance.", color: GRAPHITE },
  { icon: FaChartLine, title: "Professional Services", desc: "Practice loans, fit-outs, and technology upgrades for professionals.", color: SAND },
];

const FinanceServicesCommercial: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/commercial"
        fallbackTitle="Commercial Finance - One Finance Australia"
        fallbackDescription="Business loans, commercial property finance, equipment finance, and working capital solutions to fuel your business growth."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <HStack spacing={12} flexDir={{ base: "column", lg: "row" }} justify="space-between">
            <VStack align="start" spacing={4} flex={1}>
              <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
                Business Growth Finance
              </Badge>
              <Heading size="2xl" fontWeight="bold">
                Commercial Finance Solutions
              </Heading>
              <Text fontSize="lg" opacity={0.9} maxW="2xl">
                Fuel your business expansion with tailored commercial property loans, equipment finance, and working capital solutions.
              </Text>
            </VStack>
            <SimpleGrid columns={2} spacing={4}>
              <Box bg="rgba(255,255,255,0.1)" p={6} borderRadius="lg" textAlign="center">
                <Text fontSize="2xl" fontWeight="bold">$100K+</Text>
                <Text fontSize="sm">Loan Amounts</Text>
              </Box>
              <Box bg="rgba(255,255,255,0.1)" p={6} borderRadius="lg" textAlign="center">
                <Text fontSize="2xl" fontWeight="bold">70%</Text>
                <Text fontSize="sm">LVR Available</Text>
              </Box>
            </SimpleGrid>
          </HStack>
        </Container>
      </Box>

      {/* Business Loan Types Table */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          Commercial Loan Types
        </Heading>
        <Table variant="simple" size="lg" bg="white" borderRadius="xl" overflow="hidden" shadow="lg">
          <Thead bg={NAVY}>
            <Tr>
              <Th color="white">Loan Type</Th>
              <Th color="white">Purpose</Th>
              <Th color="white">Typical Amount</Th>
              <Th color="white">Term</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td fontWeight="bold">Commercial Property</Td>
              <Td>Purchase office, retail, or industrial property</Td>
              <Td>$500K – $10M+</Td>
              <Td>5–30 years</Td>
            </Tr>
            <Tr bg="gray.50">
              <Td fontWeight="bold">Equipment Finance</Td>
              <Td>Purchase machinery, vehicles, technology</Td>
              <Td>$10K – $5M</Td>
              <Td>1–7 years</Td>
            </Tr>
            <Tr>
              <Td fontWeight="bold">Business Overdraft</Td>
              <Td>Flexible working capital and cash flow</Td>
              <Td>$10K – $500K</Td>
              <Td>1–3 years</Td>
            </Tr>
            <Tr bg="gray.50">
              <Td fontWeight="bold">Debtor Finance</Td>
              <Td>Advance on outstanding invoices</Td>
              <Td>Up to 90% of invoices</Td>
              <Td>Ongoing</Td>
            </Tr>
            <Tr>
              <Td fontWeight="bold">Business Loan</Td>
              <Td>General business purposes, expansion, acquisition</Td>
              <Td>$50K – $5M</Td>
              <Td>1–10 years</Td>
            </Tr>
          </Tbody>
        </Table>
      </Container>

      {/* Industry Solutions */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading textAlign="center" mb={12} size="xl">
            Industry-Specific Solutions
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {industries.map((industry, idx) => (
              <Box key={idx} p={8} bg="white" borderRadius="xl" shadow="md" transition="all 0.3s" _hover={{ shadow: "xl", transform: "translateY(-4px)" }}>
                <Icon as={industry.icon} boxSize={12} color={industry.color} mb={4} />
                <Heading size="md" mb={3}>{industry.title}</Heading>
                <Text color="gray.600" fontSize="sm">{industry.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Case Studies Accordion */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          Success Stories
        </Heading>
        <Accordion allowMultiple>
          <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
            <AccordionButton p={6} _expanded={{ bg: "gray.50" }}>
              <Box flex="1" textAlign="left">
                <Heading size="md">Manufacturing Expansion – $2.5M</Heading>
                <Text fontSize="sm" color="gray.600" mt={2}>Facility purchase & equipment finance</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6}>
              <Text color="gray.700">
                A growing manufacturing business needed to expand operations. We structured a $2.5M facility including property purchase ($1.8M) and new machinery ($700K) with 70% LVR and 15-year terms. The business increased production capacity by 300%.
              </Text>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
            <AccordionButton p={6} _expanded={{ bg: "gray.50" }}>
              <Box flex="1" textAlign="left">
                <Heading size="md">Retail Portfolio Acquisition – $5.8M</Heading>
                <Text fontSize="sm" color="gray.600" mt={2}>Multi-property commercial investment</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6}>
              <Text color="gray.700">
                Investor acquired 4 retail properties across metropolitan areas. We arranged cross-collateralised lending at 65% LVR with interest-only terms, generating positive cash flow from day one. Portfolio now valued at $8.2M.
              </Text>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg">
            <AccordionButton p={6} _expanded={{ bg: "gray.50" }}>
              <Box flex="1" textAlign="left">
                <Heading size="md">Medical Practice Fit-Out – $450K</Heading>
                <Text fontSize="sm" color="gray.600" mt={2}>Equipment & renovation finance</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6}>
              <Text color="gray.700">
                New medical practice required complete fit-out including specialised equipment. Unsecured business loan with 7-year term allowed the practice to open without property security. Practice now serves 2,000+ patients annually.
              </Text>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl" textAlign="center">
          <Icon as={FaBuilding} boxSize={16} mb={6} />
          <Heading size="xl" mb={4}>Ready to Grow Your Business?</Heading>
          <Text fontSize="lg" mb={8}>Speak with a commercial finance specialist today</Text>
          <HStack justify="center" spacing={4}>
            <Box as="button" px={8} py={3} bg={SAND} color={NAVY} borderRadius="lg" fontWeight="bold">
              Get Pre-Qualified
            </Box>
            <Box as="button" px={8} py={3} bg="transparent" border="2px solid" borderColor="white" borderRadius="lg" fontWeight="bold">
              Download Business Guide
            </Box>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesCommercial;
